<template>
  <div class="contact-conversation--panel">
    <spinner v-if="showLoadingSpinner" />
    <div
      v-else-if="uiFlags.fetchError"
      class="no-label-message"
    >
      <span>{{ $t('CONTACT_PANEL.CONVERSATIONS.FETCH_ERROR') }}</span>
    </div>
    <div v-else class="contact-conversation__wrap">
      <div v-if="!previousConversations.length" class="no-label-message">
        <span>
          {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
        </span>
      </div>
      <div v-else class="contact-conversation--list">
        <div
          v-for="conversation in previousConversations"
          :key="conversation.id"
          class="previous-conversation-item flex items-start"
          @click="onConversationRowClick(conversation)"
        >
          <span
            class="conversation-inbox-icon mt-5 mr-0 rtl:ml-1 rtl:mr-0"
            :title="conversationInboxName(conversation)"
          >
            <fluent-icon
              :icon="conversationInboxIcon(conversation)"
              view-box="0 0 30 30"
              size="18"
            />
          </span>
          <conversation-card
            :chat="conversation"
            :hide-inbox-name="false"
            :hide-thumbnail="true"
            :show-assignee-name="true"
            class="compact previous-conversation-card"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard';
import { getInboxClassByType } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    ConversationCard,
    Spinner,
  },
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    conversationId: {
      type: [String, Number],
      required: true,
    },
    /** When true, emit conversation-selected instead of opening a new browser tab. */
    selectInPage: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'getContactSidebarUiFlags',
      accountId: 'getCurrentAccountId',
    }),
    conversations() {
      return this.$store.getters.getConversationsForContact(this.contactId);
    },
    previousConversations() {
      return this.conversations
        .filter(conversation => conversation.id !== Number(this.conversationId))
        .sort((a, b) => b.timestamp - a.timestamp);
    },
    /** Keep the list visible while refreshing when conversations are already loaded. */
    showLoadingSpinner() {
      return this.uiFlags.isFetching && !this.conversations.length;
    },
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.$store.dispatch('fetchContactConversations', newContactId);
      }
    },
  },
  mounted() {
    this.$store.dispatch('fetchContactConversations', this.contactId);
  },
  methods: {
    conversationInbox(conversation) {
      const storeInbox = this.$store.getters['inboxes/getInbox'](
        conversation.inbox_id
      );
      if (storeInbox && storeInbox.id) return storeInbox;
      // Agents may now reach conversations on inboxes they don't collaborate on
      // (when the contact is assigned to them). Use the conversation payload so
      // the row still renders the right channel icon / label instead of '{}'.
      return conversation.inbox || {};
    },
    conversationInboxIcon(conversation) {
      const inbox = this.conversationInbox(conversation);
      const type =
        inbox.channel_type ||
        conversation.channel_type ||
        conversation.meta?.channel ||
        '';
      const phoneNumber = inbox.phone_number || conversation.phone_number || '';
      return getInboxClassByType(type, phoneNumber);
    },
    conversationInboxName(conversation) {
      const inbox = this.conversationInbox(conversation);
      if (inbox.name) return inbox.name;
      // Prefer the inbox name carried on the payload (so agents who don't
      // collaborate on this inbox still see the real label) and only fall back
      // to the channel type when that is missing.
      if (conversation.meta?.inbox_name) return conversation.meta.inbox_name;
      const channel =
        conversation.meta?.channel?.split('::')[1] ||
        conversation.channel_type?.split('::')[1] ||
        '';
      return channel || this.$t('INBOX_MGMT.HEADER');
    },
    openInNewTab(conversation) {
      const url = `/app/accounts/${this.accountId}/conversations/${conversation.id}`;
      window.open(url, '_blank');
    },
    onConversationRowClick(conversation) {
      if (this.selectInPage) {
        this.$emit('conversation-selected', conversation);
      } else {
        this.openInNewTab(conversation);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.no-label-message {
  @apply text-slate-500 dark:text-slate-400 mb-4;
}

.previous-conversation-item {
  cursor: pointer;
}

.conversation-inbox-icon {
  @apply inline-flex items-center text-slate-500 dark:text-slate-400;
  flex-shrink: 0;
}

:deep(.previous-conversation-card.conversation) {
  width: 100% !important;
  max-width: none !important;
  flex: 1 1 auto !important;
  min-width: 0;
}

:deep(.conversation) {
  @apply pr-0;
  pointer-events: none;
  .conversation--details {
    @apply pl-2;
  }
}
</style>
